// Planning a tandem as ONE phase: the extruder that lays the inner sheath and
// the one that lays the outer are a single pass through the line.
//
// ── what already exists ───────────────────────────────────────────────────
//
// Both phases are planned separately and then fused in the launch summary:
// `redis_dev/helpers/jobHelpers.js` `fuseTandemInteriorExterior` pairs an
// exterior task to the interior tasks whose outputs it consumes, rewires the
// exterior to consume the ROPE directly, unions the two material plans, marks it
// `tandem` with `tandemPhases: [interior, exterior]`, repoints the rope's
// reservations onto the exterior task and deletes the interior tasks and their
// drums. That is the end state, and it works.
//
// What it costs is the planning: the interior has to be filled in drum by drum
// only to be deleted a moment later.
//
//   *"as vrea cumva sa detecteze automat INTERIOR_SHEAT si EXTERIOR_SHEAT si sa
//   ne puna un TOGGLE DE TANDEM … in care sa planific doar exterior_sheat … dar
//   sa functioneze cum functioneaza acuma, nu renunta la conceptul din summary"*
//   (2026-11-03)
//
// ── so this DERIVES the interior rather than replacing the mechanism ──────
//
// With the toggle on, the planner fills in the exterior against the ROPE, and
// the submit emits BOTH groups: an interior derived from those executions, and
// the exterior chained to it, plus the job in `tandemJobs`. Downstream nothing
// changes — same chaining, same fuse, same end state — which is exactly what
// makes the two routes comparable, and the planner asked to compare them.
//
// The alternative was a backend task carrying both phases from the start. It
// reads better and it would have been a different thing: there would be nothing
// to check the toggle against.
//
// Pure, and mirrored into tests/.generated by tests/tools/mirror.mjs.

export const INTERIOR = 'interior_sheat';
export const EXTERIOR = 'exterior_sheat';

const phaseByName = (product, name) =>
  (product?.phases || []).find(p => p?.name === name) || null;

/**
 * Is this product a tandem candidate — does it have BOTH sheath phases?
 *
 * This is the detection the toggle appears on. It asks the product, not the
 * product's NAME: CYYF is the usual one, but anything with an inner and an outer
 * sheath qualifies, and a recipe without the inner one never should.
 */
export function tandemPair(product) {
  const interior = phaseByName(product, INTERIOR);
  const exterior = phaseByName(product, EXTERIOR);
  if (!interior || !exterior) return null;
  return { interior, exterior };
}

/** Is the toggle relevant to the phase on screen? */
export function isTandemPhase(phaseName) {
  return phaseName === INTERIOR || phaseName === EXTERIOR;
}

/**
 * The products with the tandem rewired on or off for ONE job.
 *
 * ON  the exterior step must offer the drums the INTERIOR would have consumed —
 *     the rope — because the interior is not being planned and so has no output
 *     to select. So the exterior's `dependsOn` is pointed at the interior's, and
 *     the interior is marked `_tandemWith` so its own step can say it is being
 *     planned here instead of asking for drums nobody should type.
 * OFF everything is put back from `_tandemRestore`, which is why that is kept
 *     rather than recomputed: the exterior's real `dependsOn` is the only place
 *     the original value exists once it has been overwritten.
 *
 * Returns a NEW array; the products themselves are copied shallowly down to the
 * phases that change, so React sees a change and nothing else is disturbed.
 */
export function applyTandem(products, jobId, on) {
  const id = String(jobId ?? '');
  return (Array.isArray(products) ? products : []).map(product => {
    const pid = String(product?._id ?? product?._parentJobId ?? '');
    if (!id || pid !== id) return product;

    const pair = tandemPair(product);
    if (!pair) return product;

    return {
      ...product,
      _tandem: !!on,
      phases: (product.phases || []).map(phase => {
        if (phase?.name === EXTERIOR) {
          if (on) {
            return {
              ...phase,
              // Keep the real value — it is about to be overwritten and there is
              // nowhere else to read it back from.
              _tandemRestore: phase._tandemRestore ?? phase.dependsOn ?? null,
              dependsOn: pair.interior.dependsOn ?? null,
              _tandemWith: INTERIOR
            };
          }
          const { _tandemRestore, _tandemWith, ...rest } = phase;
          return { ...rest, dependsOn: _tandemRestore ?? phase.dependsOn ?? null };
        }

        if (phase?.name === INTERIOR) {
          if (on) return { ...phase, _tandemPlannedWith: EXTERIOR };
          const { _tandemPlannedWith, ...rest } = phase;
          return rest;
        }

        return phase;
      })
    };
  });
}

/** Is this phase the one being skipped because its tandem partner carries it? */
export function isTandemCarried(phase) {
  return !!phase?._tandemPlannedWith;
}

/**
 * The wizard id of the interior execution derived from an exterior one.
 *
 * ONE definition, used by both halves of the derivation: the backend chains a
 * downstream execution onto a specific upstream drum by matching the exterior's
 * `wizardUpstreamIds` against the interior's `executionMeta.wizardExecId`
 * (`execTokenByWizardId`, controller/jobs.js). Computing that string twice from
 * two different fields is how a two-drum run cross-wires.
 */
const interiorWizardId = e => `${e?.executionMeta?.wizardExecId ?? e?.id ?? 0}-tandem-int`;

/**
 * The interior group the submit has to send alongside an exterior one.
 *
 * Every execution keeps its machine, its drum and its length — a tandem is ONE
 * pass, so the inner sheath is laid over exactly the same metres on exactly the
 * same line, and `fuseTandemInteriorExterior` refuses to fuse across machines.
 * What it must NOT inherit is the exterior's PHASE: `nextPhaseParameters` is
 * what the backend builds `materialPlan` from, so an interior carrying the
 * outer sheath's material and diameter would plan the wrong PVC — and the fuse
 * unions the two plans, so the inner layer would vanish from the job's kg
 * rather than merely being wrong.
 *
 * Hence `interiorBase`: the interior's OWN group, built by the wizard's
 * `buildGroupForPhase` (empty of executions, since nothing was filled in). Its
 * parameters, `groupKey`, `fromPhase` and `inputTokenIds` are already right;
 * only the executions are missing, and those are the exterior's.
 *
 * @param group        the exterior group as the submit built it
 * @param interiorBase the interior phase's own group, executions empty
 * @returns the interior group, or null when there is nothing to derive
 */
export function deriveInteriorGroup(group, interiorBase) {
  const execs = Array.isArray(group?.executions) ? group.executions : [];
  if (!execs.length) return null;

  if (!interiorBase) {
    // Never fall back to the exterior group: it would submit cleanly and plan
    // the inner sheath out of the outer's material. Loud beats plausible.
    throw new Error('tandemPlan: deriveInteriorGroup needs the interior phase group');
  }

  return {
    ...interiorBase,
    nextPhase: INTERIOR,
    executions: execs.map(e => ({
      ...e,
      // A distinct wizard id per derived execution, so the exterior chains onto
      // THIS drum and not onto the whole phase's outputs.
      id: interiorWizardId(e),
      executionMeta: {
        ...(e?.executionMeta || {}),
        wizardExecId: interiorWizardId(e)
      }
    }))
  };
}

/**
 * The exterior group, chained onto the interior that was just derived from it.
 *
 * Its drum selection, its `fromPhase` and its group-level `inputTokenIds` all
 * described the ROPE, which is now the interior's business. All three have to
 * move, not just the per-execution ids:
 *
 *   - `fromPhase` is what the backend chains on (`submitOutputsByPhase` is keyed
 *     `${jobId}::${nextPhase}`) AND what it topologically sorts on. Left at
 *     `rope` the submit is accepted and nothing is chained: two tasks both eat
 *     the rope, the fuse pairs by token membership so it finds nothing, and the
 *     result is a plain exterior task — no tandem, no error.
 *   - `inputTokenIds` would otherwise still name the rope drums, and a token's
 *     `phase` is validated against `fromPhase`.
 */
export function chainExteriorOntoInterior(group) {
  const execs = Array.isArray(group?.executions) ? group.executions : [];

  return {
    ...group,
    fromPhase: INTERIOR,
    // Filled by the backend from the interior group's planned outputs.
    inputTokenIds: [],
    executions: execs.map(e => ({
      ...e,
      // Not the rope any more — the interior drum derived from this very row.
      execInputTokenIds: null,
      execInputTakes: null,
      executionMeta: {
        ...(e?.executionMeta || {}),
        wizardUpstreamIds: [interiorWizardId(e)]
      }
    }))
  };
}

/**
 * Both groups, in dependency order, for one exterior group planned in tandem.
 *
 * Order matters less than it looks — the backend topo-sorts groups by
 * `fromPhase` — but a group list that already reads in dependency order is one
 * less thing resting on that sort.
 */
export function tandemGroupsFor(group, interiorBase) {
  const interior = deriveInteriorGroup(group, interiorBase);
  if (!interior) return [group];
  return [interior, chainExteriorOntoInterior(group)];
}
